<template>
  <div data-locator="bibles-page" class="layout-spacing">
    <BiblesHeader 
      :disabled="biblesPending || hasNoData"
      :sort="sort"
      @generate-bible="onGenerateBible"
      @input="sort = $event"
    />
    
    <template v-if="hasNoData">
      <app-empty-state
        class="mt-16"
        module="bibles"
        size="sm"
        :translation-key-prefix="TRANSLATION_KEY_PREFIX"
      />
    </template>
    <template v-else>
      <app-grid 
        class="mt-8"
        :is-loading="biblesPending"
        :items-count="filteredBibles.length"
      >
        <template
          v-for="bible in filteredBibles"
          :key="`bible-${bible.id}`"
        >
          <BibleGridItem 
            :bible="bible"
            :auto-download="checkBibleAutoDownload(bible)"
            :mnemo="roomMnemo"
            @share="onShare"
            @export-to-imanage="onExportToImanage"
          />
        </template>
      </app-grid>
    </template>

    <BiblesSharePane 
      :bible="bibleToShare"
      :mnemo="roomMnemo"
      :pane-is-open="isBibleSharePaneOpen"
      @close-share-pane="isBibleSharePaneOpen = false"
      @share-success="getBibles"
    />

    <BibleGenerateDialog
      v-if="openBibleGenerateDialog"
      :mnemo="roomMnemo"
      display-tree-structure
      @poll-bibles="onPollBibles"
      @close="openBibleGenerateDialog = false"
    />

    <IManageFolderPickerDialog 
      v-if="imanageEnabledAndLogged" 
      v-model:is-open="openImanageFolderPicker" 
      @folders="prepareExportToImanage"
    />
    
    <PolyOfficeDialog 
      v-if="polyOfficeEnabled && dialogIsOpen" 
      @callback="polyOfficeDialogCallback"
    />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import { SORT_OPTIONS } from '@/common/app-sort-select/sortOptions'
import IManageFolderPickerDialog from '@/common/imanage/IManageFolderPickerDialog.vue'
import PolyOfficeDialog, { POLY_OFFICE_DIALOG_STATUS_SUCCESS } from '@/common/polyOffice/PolyOfficeDialog.vue'
import { BibleNotifications } from "@/common/utils/notifications";
import { toast } from '@/common/utils/toast'
import BibleGridItem from '@/project/bibles/BibleGridItem.vue'
import BiblesHeader from '@/project/bibles/BiblesHeader.vue'
import BiblesSharePane from '@/project/bibles/BiblesSharePane.vue'
import BibleGenerateDialog from '@/project/bibles/dialogs/bible-generation/BibleGenerateDialog.vue'
import { realtimeService } from "@/services/realtime.service";
import { BiblesActionTypes } from '@/store/modules/bibles/action_types'
import { BiblesMutationTypes } from "@/store/modules/bibles/mutation_types";


const TRANSLATION_KEY_PREFIX = 'project.bibles.Bibles'
const POLL_BIBLES_POLLING_DELAY = 5000

export default {
  name: 'Bibles',
  components: {
    BibleGridItem,
    BibleGenerateDialog,
    BiblesHeader,
    BiblesSharePane,
    IManageFolderPickerDialog,
    PolyOfficeDialog,
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      bibleToExportToImanage: null,
      bibleToShare: null,
      eventHandlers: {
        [BibleNotifications.BIBLE_CREATED]: this.handleCreationNotification,
        [BibleNotifications.BIBLE_UPDATED]: this.handleUpdateNotification,
        [BibleNotifications.BIBLE_PROGRESS]: this.handleProgressNotification,
      },
      isBibleSharePaneOpen: false,
      legacyPollingEnabled: import.meta.env.VUE_APP_ENABLE_OLD_POLLING,
      openBibleGenerateDialog: false,
      openImanageFolderPicker: false,
      pollBiblesTimeout: null,
      sort: SORT_OPTIONS.NAME,
    }
  },
  head () {
    return {
      title: this.$t(`${TRANSLATION_KEY_PREFIX}.metaTitle`).toString(),
    }
  },
  computed: {
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapGetters('room', ['roomMnemo']),
    ...mapGetters('user', ['polyOfficeEnabled']),
    ...mapState('bibles', ['bibles', 'biblesPending']),
    ...mapState('polyOffice', ['dialogIsOpen', 'dialogSuccess']),
    ...mapState('room', ['currentRoom']),
    filteredBibles () {
      let bibles = cloneDeep(this.bibles)
      if (this.sort === SORT_OPTIONS.NAME) {
        bibles = bibles.sort((a, b) => {
          return a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        })
      } else if (this.sort === SORT_OPTIONS.CREATION_DATE) {
        bibles = bibles.sort((a, b) => {
          return new Date(a.generatedAt) < new Date(b.generatedAt) ? -1 : 1
        })
      }
      return bibles
    },
    hasNoData () {
      return this.bibles && this.bibles.length === 0 && !this.biblesPending
    },
  },
  mounted () {
    this.GET_BIBLES({ mnemo: this.roomMnemo })

    if (this.legacyPollingEnabled === true) {
      if (window.history.state.poll) {
        this.pollBiblesTimeout = setTimeout(() => { this.pollBibles() }, POLL_BIBLES_POLLING_DELAY)
      }
    } else {
      realtimeService.on([
        BibleNotifications.BIBLE_CREATED,
        BibleNotifications.BIBLE_PROGRESS,
        BibleNotifications.BIBLE_UPDATED,
      ], this.handleRealtimeEvent)
    }
  },
  beforeUnmount () {
    if (this.legacyPollingEnabled === true) {
      clearTimeout(this.pollBiblesTimeout)
      // Set to a recognizable value so that we know if we should stop refreshing if already fired
      this.pollBiblesTimeout = -1
    } else {
      realtimeService.off([
        BibleNotifications.BIBLE_CREATED,
        BibleNotifications.BIBLE_PROGRESS,
        BibleNotifications.BIBLE_UPDATED,
      ], this.handleRealtimeEvent)
    }
  },
  methods: {
    ...mapMutations('bibles', [BiblesMutationTypes.SET_BIBLE_PROGRESS]),
    ...mapActions('bibles', [BiblesActionTypes.EXPORT_BIBLE_TO_IMANAGE, BiblesActionTypes.GET_BIBLES]),
    checkBibleAutoDownload (bible) {
      return bible.id.toString() === this.$route.query.idBible
    },
    getBibles () {
      return this.GET_BIBLES({ mnemo: this.roomMnemo, params: { ifRecent: true }, isPolling: true })
    },
    handleRealtimeEvent (event, data) {
      const handler = this.eventHandlers[event];

      if (handler) {
        handler(data);
      }
    },
    async handleCreationNotification () {
      await this.getBibles()
    },
    async handleUpdateNotification () {
      await this.getBibles()
    },
    async handleProgressNotification (data) {
      this.SET_BIBLE_PROGRESS({ bible: data.bible, progress: data.progress })
    },
    onExportToImanage (bibleId) {
      this.bibleToExportToImanage = bibleId
      this.openImanageFolderPicker = true
    },
    onGenerateBible (value) {
      this.openBibleGenerateDialog = value
    },
    onPollBibles () {
      setTimeout(() => { this.pollBibles() }, 500)
    },
    onShare(bible) {
      this.bibleToShare = bible
      this.isBibleSharePaneOpen = true
    },
    async pollBibles () {
      if (this.pollBiblesTimeout) {
        clearTimeout(this.pollBiblesTimeout)
        this.pollBiblesTimeout = null
      }
      try {
        await this.getBibles()
      } finally {
        if (this.bibles.filter(x => x.status === 'processing').length > 0) {
          if (this.pollBiblesTimeout !== -1) {
            this.pollBiblesTimeout = setTimeout(() => { this.pollBibles() }, POLL_BIBLES_POLLING_DELAY)
          }
        }
      }
    },
    polyOfficeDialogCallback (data) {
      if (data.status === POLY_OFFICE_DIALOG_STATUS_SUCCESS) {
        toast.success(this.dialogSuccess)
      }
    },
    async prepareExportToImanage (target) {
      const data = {
        targetId: target.selected,
      }
      try {
        await this.EXPORT_BIBLE_TO_IMANAGE({
          mnemo: this.roomMnemo,
          bibleId: this.bibleToExportToImanage,
          data,
        })
        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.exportBibleToImanageSuccess`))
      } catch (error) {
        console.error(error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.exportBibleToImanageError`))
      }
    },
  },
}
</script>